/**
 * CADINU Verified Deploy (/verified-deploy): Upload Artifact, Review, Connect Wallet, Deploy, Verify Match.
 *
 * Security model (owner's brief 2026-10-02, same principles as the V4 MetaMask deploy kit):
 *  - no key or seed ever: the connected wallet signs the one contract-creation transaction;
 *  - the artifact is read with FileReader + JSON.parse behind a size and depth limit, validated field by field, and every value
 *    is rendered as React text (no HTML injection API anywhere in this route; a CI test enforces it);
 *  - chain reads go through the wallet's provider and are shape-checked; the network is fingerprinted before review and again
 *    right before sending;
 *  - the route has its own strict Content-Security-Policy header (csp.mjs). A header only applies to a full document load, so the
 *    page reloads itself when it was reached by client-side navigation, and leaves with full loads.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Router from 'next/router'
import { useAccount, useNetwork } from 'wagmi'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { formatEther, formatGwei } from 'viem'
import { Artifact, ArtifactError, MAX_FILE_BYTES, parseArtifactText } from './logic/artifact'
import { ADDRESS_RE, compareRuntime, CompareResult, linkBytecode } from './logic/bytecode'
import { displayValue, encodeArgs, fieldHint, fieldKind, parseValueNative } from './logic/constructor'
import {
  CHAINS,
  checkNetwork,
  Eip1193,
  estimateGas,
  getAccounts,
  getBalance,
  getCode,
  getNonce,
  getReceipt,
  getTxInput,
  hashHex,
  nativeSymbol,
  NetworkCheck,
  predictAddress,
  sendDeploy,
  waitReceipt,
} from './logic/chain'
import { errorText, hasInvisible, visible } from './logic/text'
import {
  Button,
  Column,
  Dl,
  Drop,
  Eyebrow,
  ExtLink,
  Field,
  Fields,
  H1,
  Hero,
  Input,
  Lead,
  Mono,
  Muted,
  Note,
  Page,
  Panel,
  Pill,
  Promises,
  Row,
  SectionTitle,
  Select,
  StepHead,
  StepNo,
  TextArea,
  Verdict,
} from './ui'

export const ROUTE = '/verified-deploy'

type Review = {
  chainId: number
  network: NetworkCheck
  account: string
  nonce: number
  predicted: string
  data: string
  dataHash: string
  runtimeHash: string | null
  args: { name: string; type: string; text: string; invisible: boolean }[]
  libraries: { name: string; file: string; address: string; hasCode: boolean }[]
  value: bigint
  gas: bigint | null
  gasError: string | null
  gasPrice: bigint
  balance: bigint
  cost: bigint | null
  blockers: string[]
}
type Deploy = { phase: 'idle' | 'checking' | 'signing' | 'pending' | 'verifying' | 'done' | 'failed'; txHash?: string; error?: string }
type Outcome = {
  address: string
  txHash: string | null
  block: bigint | null
  gasUsed: bigint | null
  chainId: number
  compare: CompareResult
  inputMatches: boolean | null
  codeBytes: number
  predictedMatches: boolean | null
}

/** Strict CSP needs a document load of this route: reload when reached client-side, leave with full loads. */
function useStrictDocument(): boolean {
  const [ok, setOk] = useState(false)
  useEffect(() => {
    const nav = (performance.getEntriesByType?.('navigation') ?? [])[0] as PerformanceNavigationTiming | undefined
    let loadedHere = true
    try {
      loadedHere = !nav || new URL(nav.name).pathname.replace(/\/$/, '') === ROUTE
    } catch {
      loadedHere = true
    }
    if (!loadedHere) {
      window.location.replace(`${ROUTE}${window.location.search}`)
      return undefined
    }
    setOk(true)
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin || url.pathname === ROUTE) return
      e.preventDefault()
      e.stopPropagation()
      window.location.assign(url.href)
    }
    const onRoute = (url: string) => {
      if (url.split('?')[0] === ROUTE) return
      window.location.assign(url)
      Router.events.emit('routeChangeError')
      // aborts the client-side transition; the full load above takes over
      // eslint-disable-next-line no-throw-literal
      throw 'verified-deploy: leaving with a full page load'
    }
    window.addEventListener('click', onClick, true)
    Router.events.on('routeChangeStart', onRoute)
    return () => {
      window.removeEventListener('click', onClick, true)
      Router.events.off('routeChangeStart', onRoute)
    }
  }, [])
  return ok
}

/** Bez, 2026-10-02: shown under the drop zone and as the error for a dropped .sol file. */
const SOL_NOT_ACCEPTED = '.sol source is not accepted. Upload the compiled JSON (Hardhat: artifacts/…/X.json, Foundry: out/X.sol/X.json).'

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onerror = () => reject(new ArtifactError('The file could not be read.'))
    r.onload = () => (typeof r.result === 'string' ? resolve(r.result) : reject(new ArtifactError('The file could not be read as text.')))
    r.readAsText(file)
  })
}

const short = (a: string) => `${a.slice(0, 8)}...${a.slice(-6)}`
const fmtNative = (wei: bigint, chainId: number) => `${formatEther(wei)} ${nativeSymbol(chainId)}`
const explorer = (chainId: number, kind: 'address' | 'tx', v: string) => (CHAINS[chainId]?.explorer ? `${CHAINS[chainId].explorer}/${kind}/${v}` : null)
const FORMAT_LABEL: Record<Artifact['format'], string> = { forge: 'Foundry', hardhat: 'Hardhat', truffle: 'Truffle', solc: 'solc standard JSON' }

export default function VerifiedDeploy() {
  const strict = useStrictDocument()
  const { address, connector, isConnected } = useAccount()
  const { chain } = useNetwork()

  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState<string | null>(null)
  const [over, setOver] = useState(false)
  const [artifact, setArtifact] = useState<Artifact | null>(null)
  const [args, setArgs] = useState<string[]>([])
  const [libs, setLibs] = useState<string[]>([])
  const [value, setValue] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [review, setReview] = useState<Review | null>(null)
  const [reviewing, setReviewing] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [deploy, setDeploy] = useState<Deploy>({ phase: 'idle' })
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [checkRef, setCheckRef] = useState('')
  const [checkError, setCheckError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const stopWait = useRef({ stop: false })

  const provider = useCallback(async (): Promise<Eip1193> => {
    if (!connector) throw new Error('Connect a wallet first.')
    const p = (await connector.getProvider()) as Eip1193 | undefined
    if (!p || typeof p.request !== 'function') throw new Error('The connected wallet has no provider.')
    return p
  }, [connector])

  // any change to what would be deployed, or to the wallet, voids the review
  useEffect(() => {
    setReview(null)
    setReviewError(null)
  }, [artifact, args, libs, value, address, chain?.id])

  const onFile = useCallback(async (file: File | undefined | null) => {
    setArtifact(null)
    setOutcome(null)
    setDeploy({ phase: 'idle' })
    setFileError(null)
    if (!file) return
    setFileName(visible(file.name, 120))
    try {
      // a Solidity source is the most common wrong file: say what to upload instead of a generic JSON error
      if (/\.sol$/i.test(file.name)) throw new ArtifactError(SOL_NOT_ACCEPTED)
      if (file.size > MAX_FILE_BYTES) throw new ArtifactError(`The file is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`)
      const text = await readFileText(file)
      const a = parseArtifactText(text, file.name)
      setArtifact(a)
      setArgs(a.ctorInputs.map((p) => (fieldKind(p.type) === 'bool' ? 'false' : '')))
      setLibs(a.libraries.map(() => ''))
      setValue('')
    } catch (e) {
      setFileError(e instanceof ArtifactError ? e.message : 'The file could not be read as an artifact.')
    }
  }, [])

  const libMap = useMemo(() => new Map((artifact?.libraries ?? []).map((l, i) => [l.key, (libs[i] ?? '').trim()])), [artifact, libs])

  /** Deploy data and the expected runtime with libraries linked; throws a readable error when a field is wrong. */
  const build = useCallback(() => {
    if (!artifact) throw new Error('Upload an artifact first.')
    artifact.libraries.forEach((l, i) => {
      if (!ADDRESS_RE.test((libs[i] ?? '').trim())) throw new Error(`Library ${l.name}: enter its deployed address (0x and 40 hex characters).`)
    })
    const creation = linkBytecode(artifact.creation, artifact.creationLinks, libMap)
    const runtime = artifact.runtime ? linkBytecode(artifact.runtime, artifact.runtimeLinks, libMap) : null
    const enc = encodeArgs(artifact.ctorInputs, args)
    const v = artifact.ctorPayable ? parseValueNative(value) : 0n
    return { data: `0x${creation}${enc.hex}`, runtime, values: enc.values, value: v }
  }, [artifact, args, libs, libMap, value])

  const buildPreview = useMemo(() => {
    try {
      return { ok: true as const, ...build() }
    } catch (e) {
      return { ok: false as const, error: errorText(e) }
    }
  }, [build])

  const prepareReview = useCallback(async () => {
    setReviewing(true)
    setReviewError(null)
    setReview(null)
    setFormError(null)
    try {
      if (!artifact) throw new Error('Upload an artifact first.')
      let b: ReturnType<typeof build>
      try {
        b = build()
      } catch (e) {
        setFormError(errorText(e))
        throw new Error('Fix the highlighted values first.')
      }
      const p = await provider()
      const network = await checkNetwork(p)
      const accounts = await getAccounts(p)
      const account = (address ?? '').toLowerCase()
      if (!account || !accounts.includes(account)) throw new Error('The wallet did not confirm the connected account. Reconnect it.')
      const [nonce, balance, gasPrice] = [await getNonce(p, account), await getBalance(p, account), network.gasPrice]
      const libraries = await Promise.all(
        artifact.libraries.map(async (l, i) => {
          const a = (libs[i] ?? '').trim().toLowerCase()
          return { name: l.name, file: visible(l.file, 300), address: a, hasCode: (await getCode(p, a)) !== '0x' }
        }),
      )
      let gas: bigint | null = null
      let gasError: string | null = null
      try {
        gas = await estimateGas(p, { from: account, data: b.data, value: b.value })
      } catch (e) {
        gasError = errorText(e)
      }
      const cost = gas !== null ? gas * gasPrice + b.value : null
      const blockers = [...network.problems]
      libraries.filter((l) => !l.hasCode).forEach((l) => blockers.push(`Library ${l.name}: there is no contract at ${l.address} on this network.`))
      if (gasError) blockers.push(`Gas estimation failed, so the deployment would revert: ${gasError}`)
      if (cost !== null && balance < cost) blockers.push(`The wallet holds ${fmtNative(balance, network.chainId)}; about ${fmtNative(cost, network.chainId)} is needed.`)
      setReview({
        chainId: network.chainId,
        network,
        account,
        nonce,
        predicted: predictAddress(account, nonce).toLowerCase(),
        data: b.data,
        dataHash: hashHex(b.data.slice(2)),
        runtimeHash: b.runtime ? hashHex(b.runtime) : null,
        args: artifact.ctorInputs.map((inp, i) => {
          const text = displayValue(b.values[i])
          return { name: inp.name || `argument ${i + 1}`, type: inp.type, text: visible(text), invisible: hasInvisible(text) }
        }),
        libraries,
        value: b.value,
        gas,
        gasError,
        gasPrice,
        balance,
        cost,
        blockers,
      })
    } catch (e) {
      setReviewError(errorText(e))
    } finally {
      setReviewing(false)
    }
  }, [artifact, build, provider, address, libs])

  /** Reads the code at `addr` and compares it with the artifact; optionally checks the transaction input. */
  const verifyAt = useCallback(
    async (p: Eip1193, addr: string, tx: { hash: string; block: bigint; gasUsed: bigint } | null, expectedData: string | null, predicted: string | null) => {
      if (!artifact) throw new Error('Upload the artifact first.')
      const runtime = artifact.runtime ? linkBytecode(artifact.runtime, artifact.runtimeLinks, libMap) : null
      const code = await getCode(p, addr)
      const chainId = Number(await p.request({ method: 'eth_chainId' }))
      let inputMatches: boolean | null = null
      if (tx && expectedData) {
        const t = await getTxInput(p, tx.hash)
        inputMatches = !!t && t.to === null && t.input === expectedData.toLowerCase()
      }
      setOutcome({
        address: addr,
        txHash: tx?.hash ?? null,
        block: tx?.block ?? null,
        gasUsed: tx?.gasUsed ?? null,
        chainId,
        compare: compareRuntime(runtime, code, artifact.immutables, addr),
        inputMatches,
        codeBytes: (code.length - 2) / 2,
        predictedMatches: predicted ? predicted === addr : null,
      })
    },
    [artifact, libMap],
  )

  const doDeploy = useCallback(async () => {
    if (!review || review.blockers.length || review.gas === null) return
    setOutcome(null)
    setDeploy({ phase: 'checking' })
    stopWait.current = { stop: false }
    try {
      const p = await provider()
      // the same checks again, right before the wallet opens: network, account, nonce
      const network = await checkNetwork(p)
      if (!network.ok) throw new Error(`Network check failed: ${network.problems.join(' ')}`)
      if (network.chainId !== review.chainId) throw new Error('The wallet switched network after the review. Prepare the review again.')
      const accounts = await getAccounts(p)
      if (!accounts.includes(review.account)) throw new Error('The wallet switched account after the review. Prepare the review again.')
      const nonce = await getNonce(p, review.account)
      if (nonce !== review.nonce) throw new Error(`The account's nonce moved (${review.nonce} to ${nonce}), so the predicted address changed. Prepare the review again.`)
      setDeploy({ phase: 'signing' })
      const gas = (review.gas * 125n) / 100n
      const txHash = await sendDeploy(p, { from: review.account, data: review.data, value: review.value, gas })
      setDeploy({ phase: 'pending', txHash })
      const r = await waitReceipt(p, txHash, { signal: stopWait.current })
      if (r.status !== 'success' || !r.contractAddress) throw new Error(`The transaction ${txHash} reverted; no contract was created.`)
      setDeploy({ phase: 'verifying', txHash })
      await verifyAt(p, r.contractAddress, { hash: txHash, block: r.blockNumber, gasUsed: r.gasUsed }, review.data, review.predicted)
      setDeploy({ phase: 'done', txHash })
    } catch (e) {
      setDeploy((d) => ({ phase: 'failed', txHash: d.txHash, error: errorText(e) }))
    }
  }, [review, provider, verifyAt])

  useEffect(
    () => () => {
      stopWait.current.stop = true
    },
    [],
  )

  /** Resume: a transaction hash or an address from an earlier session, compared with the uploaded artifact. */
  const checkExisting = useCallback(async () => {
    setCheckError(null)
    setChecking(true)
    try {
      const ref = checkRef.trim()
      const p = await provider()
      if (/^0x[0-9a-fA-F]{64}$/.test(ref)) {
        const r = await getReceipt(p, ref.toLowerCase())
        if (!r) throw new Error('No receipt for that transaction on the wallet’s current network (yet).')
        if (r.status !== 'success' || !r.contractAddress) throw new Error('That transaction did not create a contract.')
        let expected: string | null = null
        try {
          expected = build().data
        } catch {
          expected = null
        }
        await verifyAt(p, r.contractAddress, { hash: ref.toLowerCase(), block: r.blockNumber, gasUsed: r.gasUsed }, expected, null)
      } else if (ADDRESS_RE.test(ref)) {
        await verifyAt(p, ref.toLowerCase(), null, null, null)
      } else throw new Error('Paste a transaction hash (0x and 64 hex characters) or a contract address (0x and 40).')
    } catch (e) {
      setCheckError(errorText(e))
    } finally {
      setChecking(false)
    }
  }, [checkRef, provider, build, verifyAt])

  const step = (n: number): 'todo' | 'active' | 'done' => {
    const reached = [true, !!artifact, !!artifact, !!artifact, !!review, !!outcome]
    const done = [!!artifact, !!artifact, !!artifact && buildPreview.ok, !!review && !review.blockers.length, !!outcome, !!outcome]
    return done[n - 1] ? 'done' : reached[n - 1] ? 'active' : 'todo'
  }
  const busy = ['checking', 'signing', 'pending', 'verifying'].includes(deploy.phase)
  const sym = review ? nativeSymbol(review.chainId) : ''

  return (
    <Page data-testid="verified-deploy" data-strict={strict ? 'true' : 'false'}>
      <Column>
        <Hero>
          <Eyebrow>CADINU Verified Deploy</Eyebrow>
          <H1>Deploy a contract and prove it is the one you built</H1>
          <Lead>
            Upload a compiled artifact, review every value, sign in your own wallet, then read the code back from the chain and compare it byte for byte.
          </Lead>
          <Promises>
            <li>No private keys: your wallet signs</li>
            <li>The file is read as data, never run</li>
            <li>No third-party scripts on this page</li>
          </Promises>
        </Hero>

        {/* 1. Upload */}
        <Panel as="section" aria-labelledby="vd-upload">
          <StepHead>
            <StepNo $state={step(1)}>1</StepNo>
            <SectionTitle id="vd-upload">Upload Contract</SectionTitle>
          </StepHead>
          <Drop
            $over={over}
            $error={!!fileError}
            data-testid="vd-drop"
            onDragOver={(e) => {
              e.preventDefault()
              setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setOver(false)
              onFile(e.dataTransfer.files?.[0])
            }}
          >
            <input
              type="file"
              accept=".json,application/json"
              data-testid="vd-file"
              disabled={!strict}
              onChange={(e) => {
                const el = e.currentTarget
                onFile(el.files?.[0])
                el.value = '' // the same file can be chosen again
              }}
            />
            <strong>{fileName && artifact ? fileName : 'Drop artifact.json here'}</strong>
            <span>or tap to choose a file. Foundry, Hardhat or Truffle artifact, up to 5 MB.</span>
            <span data-testid="vd-sol-hint">{SOL_NOT_ACCEPTED}</span>
          </Drop>
          {fileError ? (
            <Note $tone="bad" role="alert" data-testid="vd-file-error" style={{ marginTop: 12 }}>
              <b>Not accepted.</b> {fileError}
            </Note>
          ) : null}
        </Panel>

        {/* 2. Details */}
        <Panel as="section" aria-labelledby="vd-details">
          <StepHead>
            <StepNo $state={step(2)}>2</StepNo>
            <SectionTitle id="vd-details">Contract Details</SectionTitle>
          </StepHead>
          {artifact ? (
            <Dl data-testid="vd-details">
              <div>
                <dt>Contract</dt>
                <dd data-testid="vd-name">
                  {artifact.name} {artifact.isLibrary ? <Pill $tone="info">library</Pill> : null}
                </dd>
              </div>
              <div>
                <dt>Artifact</dt>
                <dd>
                  {FORMAT_LABEL[artifact.format]}
                  {artifact.compiler ? `, solc ${artifact.compiler}` : ''}
                  {artifact.sourcePath ? `, ${visible(artifact.sourcePath, 300)}` : ''}
                </dd>
              </div>
              <div>
                <dt>Constructor</dt>
                <dd>
                  <Mono data-testid="vd-ctor">
                    constructor({artifact.ctorInputs.map((p) => `${p.type}${p.name ? ` ${p.name}` : ''}`).join(', ')}){artifact.ctorPayable ? ' payable' : ''}
                  </Mono>
                </dd>
              </div>
              <div>
                <dt>Creation code</dt>
                <dd>{(artifact.creation.length / 2).toLocaleString('en-US')} bytes</dd>
              </div>
              <div>
                <dt>Runtime code</dt>
                <dd>{artifact.runtime ? `${(artifact.runtime.length / 2).toLocaleString('en-US')} bytes, will be compared after deployment` : 'not in the artifact: the result cannot be verified'}</dd>
              </div>
              <div>
                <dt>Immutables</dt>
                <dd>
                  {artifact.immutables.status === 'listed'
                    ? `${artifact.immutables.ranges.length} slots, masked in the comparison`
                    : artifact.immutables.status === 'none'
                    ? 'none'
                    : 'not listed in this artifact: compared without immutable masking'}
                </dd>
              </div>
              <div>
                <dt>Libraries</dt>
                <dd>{artifact.libraries.length ? artifact.libraries.map((l) => l.name).join(', ') : 'none'}</dd>
              </div>
              <div>
                <dt>ABI</dt>
                <dd>
                  {artifact.functionCount} functions, {artifact.eventCount} events
                </dd>
              </div>
            </Dl>
          ) : (
            <Muted>Upload an artifact to see what it contains.</Muted>
          )}
        </Panel>

        {/* 3. Arguments */}
        <Panel as="section" aria-labelledby="vd-args">
          <StepHead>
            <StepNo $state={step(3)}>3</StepNo>
            <SectionTitle id="vd-args">Constructor Arguments</SectionTitle>
          </StepHead>
          {artifact ? (
            <Fields>
              {!artifact.ctorInputs.length && !artifact.libraries.length && !artifact.ctorPayable ? <Muted>This contract takes no constructor arguments.</Muted> : null}
              {artifact.ctorInputs.map((p, i) => {
                const k = fieldKind(p.type)
                const id = `vd-arg-${i}`
                const set = (v: string) => setArgs((prev) => prev.map((x, j) => (j === i ? v : x)))
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Field key={i} htmlFor={id}>
                    <span>
                      <b>{p.name || `argument ${i + 1}`}</b> {p.type}
                    </span>
                    {k === 'bool' ? (
                      <Select id={id} data-testid={id} value={args[i] ?? 'false'} onChange={(e) => set(e.target.value)}>
                        <option value="false">false</option>
                        <option value="true">true</option>
                      </Select>
                    ) : k === 'json' || k === 'string' ? (
                      <TextArea id={id} data-testid={id} value={args[i] ?? ''} placeholder={fieldHint(p)} spellCheck={false} onChange={(e) => set(e.target.value)} />
                    ) : (
                      <Input id={id} data-testid={id} value={args[i] ?? ''} placeholder={fieldHint(p)} spellCheck={false} autoComplete="off" onChange={(e) => set(e.target.value)} />
                    )}
                  </Field>
                )
              })}
              {artifact.libraries.map((l, i) => {
                const id = `vd-lib-${i}`
                return (
                  <Field key={l.key} htmlFor={id}>
                    <span>
                      <b>Library {l.name}</b> deployed address ({visible(l.file, 200)})
                    </span>
                    <Input
                      id={id}
                      data-testid={id}
                      value={libs[i] ?? ''}
                      placeholder="0x address of the deployed library on this network"
                      spellCheck={false}
                      autoComplete="off"
                      onChange={(e) => setLibs((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </Field>
                )
              })}
              {artifact.ctorPayable ? (
                <Field htmlFor="vd-value">
                  <span>
                    <b>Value</b> sent with the deployment (payable constructor), in the network&apos;s coin
                  </span>
                  <Input id="vd-value" data-testid="vd-value" value={value} placeholder="0" inputMode="decimal" onChange={(e) => setValue(e.target.value)} />
                </Field>
              ) : null}
              {formError ? (
                <Note $tone="bad" role="alert" data-testid="vd-form-error">
                  {formError}
                </Note>
              ) : null}
            </Fields>
          ) : (
            <Muted>Fields appear here once the artifact is read.</Muted>
          )}
        </Panel>

        {/* 4. Review */}
        <Panel as="section" aria-labelledby="vd-review">
          <StepHead>
            <StepNo $state={step(4)}>4</StepNo>
            <SectionTitle id="vd-review">Review Deployment</SectionTitle>
          </StepHead>
          <Fields>
            {!isConnected ? (
              <>
                <Muted>Connect the wallet that will deploy. Choose the network in the wallet; this page follows it.</Muted>
                <Row>
                  <ConnectWalletButton data-testid="vd-connect" />
                </Row>
              </>
            ) : (
              <Row>
                <Button type="button" data-testid="vd-prepare" disabled={!artifact || reviewing || busy} onClick={prepareReview}>
                  {reviewing ? 'Reading the network...' : review ? 'Refresh review' : 'Prepare review'}
                </Button>
                <Muted>
                  Wallet <Mono>{address ? short(address) : ''}</Mono> on {chain ? `${chain.name} (${chain.id})` : 'an unknown network'}
                </Muted>
              </Row>
            )}
            {reviewError ? (
              <Note $tone="bad" role="alert" data-testid="vd-review-error">
                {reviewError}
              </Note>
            ) : null}
            {review ? (
              <>
                <Dl data-testid="vd-review-table">
                  <div>
                    <dt>Contract</dt>
                    <dd>{artifact?.name}</dd>
                  </div>
                  <div>
                    <dt>Network</dt>
                    <dd data-testid="vd-network">
                      {review.network.name} (chain {review.chainId}), block {review.network.latestBlock.toString()},{' '}
                      {review.network.genesisStatus === 'match' ? 'genesis checked' : review.network.genesisStatus === 'mismatch' ? 'GENESIS DIFFERS' : 'genesis not on file'}
                    </dd>
                  </div>
                  <div>
                    <dt>Deployer wallet</dt>
                    <dd>
                      <Mono data-testid="vd-deployer">{review.account}</Mono>, nonce {review.nonce}, balance {fmtNative(review.balance, review.chainId)}
                    </dd>
                  </div>
                  <div>
                    <dt>Predicted address</dt>
                    <dd>
                      <Mono data-testid="vd-predicted">{review.predicted}</Mono>
                    </dd>
                  </div>
                  <div>
                    <dt>Constructor arguments</dt>
                    <dd data-testid="vd-review-args">
                      {review.args.length
                        ? review.args.map((a) => (
                            <div key={a.name}>
                              <b>{a.name}</b> ({a.type}): <Mono>{a.text}</Mono> {a.invisible ? <Pill $tone="warn">hidden characters shown as \u</Pill> : null}
                            </div>
                          ))
                        : 'none'}
                    </dd>
                  </div>
                  <div>
                    <dt>Libraries</dt>
                    <dd>
                      {review.libraries.length
                        ? review.libraries.map((l) => (
                            <div key={l.name}>
                              {l.name}: <Mono>{l.address}</Mono> {l.hasCode ? <Pill $tone="ok">has code</Pill> : <Pill $tone="bad">no code</Pill>}
                            </div>
                          ))
                        : 'none'}
                    </dd>
                  </div>
                  {review.value > 0n ? (
                    <div>
                      <dt>Value</dt>
                      <dd>{fmtNative(review.value, review.chainId)}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Bytecode hash</dt>
                    <dd>
                      <Mono data-testid="vd-data-hash">{review.dataHash}</Mono>
                      <Muted>keccak256 of the exact transaction data ({((review.data.length - 2) / 2).toLocaleString('en-US')} bytes: linked creation code and arguments)</Muted>
                    </dd>
                  </div>
                  <div>
                    <dt>Expected runtime hash</dt>
                    <dd>
                      <Mono>{review.runtimeHash ?? 'none in the artifact'}</Mono>
                    </dd>
                  </div>
                  <div>
                    <dt>Estimated gas</dt>
                    <dd data-testid="vd-gas">
                      {review.gas !== null
                        ? `${review.gas.toLocaleString('en-US')} gas at ${formatGwei(review.gasPrice)} gwei, about ${fmtNative(review.gas * review.gasPrice, review.chainId)}`
                        : 'not available'}
                    </dd>
                  </div>
                </Dl>
                {review.network.warnings.map((w) => (
                  <Note key={w} $tone="warn">
                    <b>Check:</b> {w}
                  </Note>
                ))}
                {review.blockers.map((b) => (
                  <Note key={b} $tone="bad" role="alert" data-testid="vd-blocker">
                    <b>Stop.</b> {b}
                  </Note>
                ))}
              </>
            ) : null}
          </Fields>
        </Panel>

        {/* 5. Deploy */}
        <Panel as="section" aria-labelledby="vd-deploy">
          <StepHead>
            <StepNo $state={step(5)}>5</StepNo>
            <SectionTitle id="vd-deploy">Connect Wallet / Deploy</SectionTitle>
          </StepHead>
          <Fields>
            <Muted>
              Your wallet opens with one contract-creation transaction (no recipient). Check that it shows the same network, account and value as the review, then
              confirm it there. This page never sees a key.
            </Muted>
            <Row>
              <Button type="button" data-testid="vd-deploy" disabled={!review || review.blockers.length > 0 || review.gas === null || busy || deploy.phase === 'done'} onClick={doDeploy}>
                {deploy.phase === 'signing'
                  ? 'Confirm in your wallet...'
                  : deploy.phase === 'pending'
                  ? 'Waiting for the block...'
                  : deploy.phase === 'verifying'
                  ? 'Reading the code back...'
                  : deploy.phase === 'checking'
                  ? 'Checking the network...'
                  : review && review.cost !== null
                  ? `Deploy (about ${formatEther(review.cost)} ${sym})`
                  : 'Deploy'}
              </Button>
            </Row>
            {deploy.txHash ? (
              <Muted>
                Transaction <Mono data-testid="vd-tx">{deploy.txHash}</Mono>
              </Muted>
            ) : null}
            {deploy.phase === 'failed' ? (
              <Note $tone="bad" role="alert" data-testid="vd-deploy-error">
                {deploy.error}
              </Note>
            ) : null}
          </Fields>
        </Panel>

        {/* 6. Result */}
        <Panel as="section" aria-labelledby="vd-result">
          <StepHead>
            <StepNo $state={step(6)}>6</StepNo>
            <SectionTitle id="vd-result">Deployment Result</SectionTitle>
          </StepHead>
          <Fields>
            {outcome ? <Result o={outcome} artifact={artifact} /> : <Muted>The verdict appears here after the deployment, read from the chain.</Muted>}
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 14 }}>Already deployed? Check a transaction or an address</summary>
              <Fields style={{ marginTop: 12 }}>
                <Muted>Uses the uploaded artifact, the library addresses above and the wallet&apos;s current network. Nothing is sent.</Muted>
                <Input data-testid="vd-check-ref" value={checkRef} placeholder="0x transaction hash or contract address" spellCheck={false} onChange={(e) => setCheckRef(e.target.value)} />
                <Row>
                  <Button type="button" $variant="ghost" data-testid="vd-check" disabled={!artifact || !isConnected || checking} onClick={checkExisting}>
                    {checking ? 'Reading...' : 'Check against the artifact'}
                  </Button>
                </Row>
                {checkError ? (
                  <Note $tone="bad" role="alert" data-testid="vd-check-error">
                    {checkError}
                  </Note>
                ) : null}
              </Fields>
            </details>
          </Fields>
        </Panel>
      </Column>
    </Page>
  )
}

function Result({ o, artifact }: { o: Outcome; artifact: Artifact | null }) {
  const c = o.compare
  const addrLink = explorer(o.chainId, 'address', o.address)
  const txLink = o.txHash ? explorer(o.chainId, 'tx', o.txHash) : null
  const how =
    c.method === 'immutables-masked'
      ? `${c.maskedSlots} immutable slots masked on both sides`
      : c.method === 'no-immutables'
      ? 'the artifact lists no immutables'
      : 'compared without immutable masking (the artifact has no immutableReferences)'
  return (
    <>
      {c.verdict === 'match' && o.inputMatches === false ? (
        <Verdict $tone="bad" data-testid="vd-verdict" data-verdict="mismatch">
          <strong>BYTECODE MISMATCH</strong>
          <Muted>
            The runtime code matches, but the transaction that created it carried other data than the review (code or constructor arguments changed between review
            and signing). Do not use or announce this address.
          </Muted>
        </Verdict>
      ) : c.verdict === 'match' ? (
        <Verdict $tone="ok" data-testid="vd-verdict" data-verdict="match">
          <strong>VERIFIED DEPLOYMENT</strong>
          <em>EXACT MATCH</em>
          <Muted>
            The {c.actualBytes.toLocaleString('en-US')}-byte runtime on chain equals the artifact&apos;s runtime ({how}
            {c.libraryAddressLinked ? ", the library's own address linked" : ''}
            {artifact?.libraries.length ? ', libraries linked' : ''}, metadata included).
          </Muted>
        </Verdict>
      ) : c.verdict === 'mismatch' ? (
        <Verdict $tone="bad" data-testid="vd-verdict" data-verdict="mismatch">
          <strong>BYTECODE MISMATCH</strong>
          <Muted>
            On chain {c.actualBytes.toLocaleString('en-US')} bytes, artifact {c.expectedBytes.toLocaleString('en-US')} bytes; first difference at byte {c.firstDiff}. ({how}.)
            {c.onlyMetadataDiffers ? ' Only the metadata tail differs: the code was built from other sources or settings than this artifact.' : ''} Do not use or announce
            this address until you know why.
          </Muted>
        </Verdict>
      ) : c.verdict === 'no-expected' ? (
        <Verdict $tone="warn" data-testid="vd-verdict" data-verdict="no-expected">
          <strong>DEPLOYED, NOT VERIFIED</strong>
          <Muted>The artifact has no runtime bytecode to compare with. Upload an artifact that includes deployedBytecode to verify.</Muted>
        </Verdict>
      ) : (
        <Verdict $tone="bad" data-testid="vd-verdict" data-verdict="no-code">
          <strong>NO CODE AT THIS ADDRESS</strong>
          <Muted>The address holds no contract on the wallet&apos;s current network.</Muted>
        </Verdict>
      )}
      <Dl data-testid="vd-result-table">
        <div>
          <dt>Contract address</dt>
          <dd>
            <Mono data-testid="vd-address">{o.address}</Mono> {addrLink ? <ExtLink href={addrLink} target="_blank" rel="noopener noreferrer">explorer</ExtLink> : null}
          </dd>
        </div>
        {o.txHash ? (
          <div>
            <dt>Transaction</dt>
            <dd>
              <Mono>{o.txHash}</Mono> {txLink ? <ExtLink href={txLink} target="_blank" rel="noopener noreferrer">explorer</ExtLink> : null}
            </dd>
          </div>
        ) : null}
        {o.block !== null ? (
          <div>
            <dt>Block, gas used</dt>
            <dd>
              {o.block.toString()}, {o.gasUsed?.toLocaleString('en-US')}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Network</dt>
          <dd>
            {CHAINS[o.chainId]?.name ?? `Chain ${o.chainId}`} ({o.chainId})
          </dd>
        </div>
        {o.inputMatches !== null ? (
          <div>
            <dt>Transaction data</dt>
            <dd data-testid="vd-input-check">{o.inputMatches ? 'equals the reviewed bytecode and arguments' : 'DIFFERS from the reviewed bytecode and arguments'}</dd>
          </div>
        ) : null}
        {o.predictedMatches !== null ? (
          <div>
            <dt>Predicted address</dt>
            <dd>{o.predictedMatches ? 'as predicted' : 'differs from the prediction (the nonce moved); the actual address above is the one'}</dd>
          </div>
        ) : null}
      </Dl>
    </>
  )
}
